import { CHECKOUT_TRACKING_KEYS } from "./checkout-tracking.js";
import {
  getPrimaryCartDiscountCode,
  hasAppliedCartDiscountCode,
} from "./cart-discount-codes.js";
import {
  estimateRewardOrderDiscountRows,
  estimateRewardProductDiscountRows,
  getBestAchievedRewardOrderDiscountTier,
  getRewardTierBillLabel,
} from "./reward-bar.js";

function normalizeTitle(title) {
  const raw = String(title || "").trim();
  if (!raw) return "Discount";
  const parts = raw.split(" — ");
  return parts.length > 1 ? parts[parts.length - 1].trim() : raw;
}

function isRewardProductCartLine(item) {
  return Boolean(item?.properties?.[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID]);
}

/** Product-tier savings already applied by Shopify on reward lines. */
function sumRewardProductLineDiscountCents(cart) {
  let sum = 0;

  for (const item of cart?.items || []) {
    if (!isRewardProductCartLine(item)) continue;

    const originalLine = Number(item.original_line_price ?? item.line_price ?? 0);
    const finalLine = Number(
      item.final_line_price ?? item.line_price ?? originalLine
    );

    if (originalLine > finalLine) {
      sum += originalLine - finalLine;
    }
  }

  return sum;
}

/** Extra product-tier savings estimated when Shopify has not applied them yet. */
function sumEstimatedRewardProductDiscountCents(cart, options = {}) {
  const setting = options.rewardSection?.setting;
  if (!setting) return 0;
  return estimateRewardProductDiscountRows(
    cart,
    setting,
    options.rewardMatchOptions
  ).reduce(
    (sum, row) => sum + (Number(row.amountCents) || 0),
    0
  );
}

function resolveAppliedCodeTitle(cart, code, options = {}) {
  const normalizedCode = String(code || "").trim();
  const labels = options.discountCodeLabels || {};
  const labelKey = normalizedCode.toLowerCase();
  if (labels[labelKey]) {
    return normalizeTitle(labels[labelKey]);
  }

  const cartLevelApps = cart?.cart_level_discount_applications || [];
  const codeMatch = cartLevelApps.find((app) => {
    const text = String(app?.title || app?.description || "").toLowerCase();
    return text.includes(labelKey);
  });
  if (codeMatch) {
    return normalizeTitle(codeMatch.title || codeMatch.description);
  }

  const titled = cartLevelApps.find((app) => app?.title || app?.description);
  if (titled) {
    return normalizeTitle(titled.title || titled.description);
  }

  return normalizedCode || "Discount";
}

/**
 * Prefer merchant reward-bar label (Discount message → Reward description)
 * over a stale Shopify automatic-discount title when the tier is achieved.
 */
function resolveRewardOrderDiscountTitle(cart, options = {}) {
  const rewardSection = options.rewardSection;
  if (!rewardSection?.setting) return null;
  const bestTier = getBestAchievedRewardOrderDiscountTier(
    cart,
    rewardSection.setting,
    options.rewardMatchOptions
  );
  if (!bestTier) return null;
  return normalizeTitle(getRewardTierBillLabel(bestTier));
}

function resolveOrderDiscountTitle(cart, options = {}) {
  const appliedCode = hasAppliedCartDiscountCode(cart)
    ? getPrimaryCartDiscountCode(cart)
    : null;
  if (appliedCode) {
    return resolveAppliedCodeTitle(cart, appliedCode, options);
  }

  // Popup config wins over Shopify discount title so editor text updates
  // after save/publish without waiting for Shopify title rewrite.
  const rewardTitle = resolveRewardOrderDiscountTitle(cart, options);
  if (rewardTitle) return rewardTitle;

  const cartLevelApps = cart.cart_level_discount_applications || [];
  if (cartLevelApps.length > 0) {
    const titled = cartLevelApps.find(
      (app) => app?.title || app?.description
    );
    if (titled) {
      return normalizeTitle(titled.title || titled.description);
    }
  }

  return "Discount";
}

function buildAppliedCodeRow(cart, options = {}, orderDiscountCents = 0) {
  const code = getPrimaryCartDiscountCode(cart);
  const title = resolveAppliedCodeTitle(cart, code, options);

  return {
    title,
    code,
    amountCents: orderDiscountCents,
    removable: true,
    applied: true,
    showAmount: orderDiscountCents > 0,
  };
}

/**
 * Discount names on one cart line (Buy 1 Get 1, line codes).
 * Order / cart-level % (e.g. 15% OFF) stay out — those only belong in Bill Summary.
 * Skip zero-amount rows so the "buy" line does not repeat BXGY when only the
 * free line actually received the cut.
 */
export function getCartLineDiscountLabels(item, cart = null) {
  const cartLevelTitles = new Set();
  for (const app of cart?.cart_level_discount_applications || []) {
    const key = String(app?.title || app?.description || "")
      .trim()
      .toLowerCase();
    if (key) cartLevelTitles.add(key);
  }

  const labels = [];
  const seen = new Set();
  const push = (title, amountCents) => {
    const label = String(title || "").trim();
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return;
    if (!(Math.abs(Number(amountCents) || 0) > 0)) return;
    if (key && cartLevelTitles.has(key)) return;
    seen.add(key);
    labels.push(label);
  };

  const allocations = Array.isArray(item?.line_level_discount_allocations)
    ? item.line_level_discount_allocations
    : [];
  if (allocations.length) {
    for (const row of allocations) {
      push(
        row?.discount_application?.title || row?.title,
        row?.amount
      );
    }
  }

  for (const row of Array.isArray(item?.discounts) ? item.discounts : []) {
    push(row?.title, row?.amount);
  }
  return labels;
}

/**
 * One row per applied Shopify discount, using the code or discount name.
 * Cart-level allocations win so the same title is not counted again on the line.
 */
export function getNamedAppliedDiscountRows(cart) {
  const grouped = new Map();
  const add = (title, cents, meta = {}) => {
    const label = String(title || "").trim();
    const amount = Math.abs(Number(cents) || 0);
    if (!label || amount <= 0) return;
    const key = label.toLowerCase();
    const current = grouped.get(key) || {
      title: label,
      amountCents: 0,
      removable: false,
      code: null,
    };
    current.amountCents += amount;
    if (meta.removable) current.removable = true;
    if (meta.code) current.code = meta.code;
    grouped.set(key, current);
  };

  const cartLevelTitles = new Set();
  for (const app of cart?.cart_level_discount_applications || []) {
    const title = app?.title || app?.description;
    const key = String(title || "").trim().toLowerCase();
    if (key) cartLevelTitles.add(key);
    const isCode = String(app?.type || "")
      .toLowerCase()
      .includes("discount_code");
    add(title, app?.total_allocated_amount, {
      removable: isCode,
      code: isCode ? title : null,
    });
  }

  for (const item of cart?.items || []) {
    const allocations = Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];
    // cart.js sends the same line discount in both arrays. Use one source.
    const lineRows = allocations.length
      ? allocations.map((row) => ({
          title: row?.discount_application?.title || row?.title,
          amount: row?.amount,
          type: row?.discount_application?.type,
        }))
      : Array.isArray(item?.discounts)
        ? item.discounts
        : [];
    for (const row of lineRows) {
      const title = String(row?.title || "").trim();
      if (cartLevelTitles.has(title.toLowerCase())) continue;
      const isCode = String(row?.type || "")
        .toLowerCase()
        .includes("discount_code");
      add(title, row?.amount, {
        removable: isCode,
        code: isCode ? title : null,
      });
    }
  }

  return [...grouped.values()];
}

/** Line-level product discounts (Buy X Get Y, line codes). Not order tiers. */
function sumLineLevelDiscountCents(cart) {
  let sum = 0;
  for (const item of cart?.items || []) {
    const allocations = Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];
    if (allocations.length) {
      for (const row of allocations) {
        sum += Math.abs(Number(row?.amount) || 0);
      }
      continue;
    }
    for (const row of Array.isArray(item?.discounts) ? item.discounts : []) {
      sum += Math.abs(Number(row?.amount) || 0);
    }
  }
  return sum;
}

function sumCartLevelDiscountCents(cart) {
  return (cart?.cart_level_discount_applications || []).reduce((sum, app) => {
    return sum + Math.abs(Number(app?.total_allocated_amount) || 0);
  }, 0);
}

/**
 * Build discount rows for cart drawer footer (Shopify cart.js shape).
 * Shows one order-level chip — product-tier savings stay on the line item.
 */
export function getCartDiscountDisplayRows(cart, options = {}) {
  if (!cart || typeof cart !== "object") {
    return { rows: [], totalDiscountCents: 0 };
  }

  const hasAppliedCode = hasAppliedCartDiscountCode(cart);
  const shopifyTotalDiscount = Number(cart.total_discount) || 0;
  const rewardProductDiscountCents = sumRewardProductLineDiscountCents(cart);
  const lineLevelDiscountCents = sumLineLevelDiscountCents(cart);
  const cartLevelOrderCents = sumCartLevelDiscountCents(cart);
  // Prefer cart-level apps. Line BXGY must not look like an order discount or
  // the reward-bar estimate is skipped and the payable total stays wrong.
  let orderDiscountCents =
    cartLevelOrderCents > 0
      ? cartLevelOrderCents
      : Math.max(
          0,
          shopifyTotalDiscount - lineLevelDiscountCents - rewardProductDiscountCents
        );

  if (hasAppliedCode) {
    return {
      rows: [buildAppliedCodeRow(cart, options, orderDiscountCents)],
      totalDiscountCents: orderDiscountCents,
    };
  }

  if (cartLevelOrderCents <= 0 && options.rewardSection?.setting) {
    const estimated = estimateRewardOrderDiscountRows(
      cart,
      options.rewardSection.setting,
      options.rewardMatchOptions
    );
    if (estimated.length) {
      return {
        rows: estimated.map((row) => ({
          ...row,
          removable: false,
          applied: false,
          source: "reward_estimate",
        })),
        totalDiscountCents: estimated.reduce(
          (sum, row) => sum + row.amountCents,
          0
        ),
      };
    }
  }

  if (orderDiscountCents <= 0) {
    return { rows: [], totalDiscountCents: 0 };
  }

  const title = resolveOrderDiscountTitle(cart, options);
  const appliedCode = hasAppliedCartDiscountCode(cart)
    ? getPrimaryCartDiscountCode(cart)
    : null;
  const hasRemovableCodeDiscount =
    hasAppliedCartDiscountCode(cart) ||
    (cart.cart_level_discount_applications || []).some((app) =>
      String(app?.type || "")
        .toLowerCase()
        .includes("discount_code")
    );
  const rows = [
    {
      title,
      amountCents: orderDiscountCents,
      code: appliedCode,
      removable: hasRemovableCodeDiscount,
      applied: Boolean(appliedCode),
      showAmount: true,
    },
  ];

  return { rows, totalDiscountCents: orderDiscountCents };
}
